/*
 * Footer — H-Plate public site footer.
 * The photo credits are a licence requirement for the Creative Commons
 * photos in /public/images; keep them while those photos are used.
 */
import { Link } from "wouter";
import { Facebook, Instagram } from "lucide-react";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/lib/storeLinks";
import { FACEBOOK_URL, INSTAGRAM_URL } from "@/lib/socialLinks";

const linkClass =
  "text-[15px] text-white/70 hover:text-white transition-colors no-underline";

const headingClass =
  "font-sans text-xs font-semibold uppercase tracking-[0.16em] text-white/45 mb-4";

/**
 * Site-wide footer with navigation, legal links and photo credits.
 */
export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#04241b] text-white">
      <div className="container pt-16 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-8 gap-y-10">
          <div className="col-span-2 md:col-span-5">
            <Link href="/" className="inline-flex items-center gap-2.5 no-underline">
              <img src="/logo-transparent.png" alt="" width={36} height={36} className="w-9 h-9 object-contain" />
              <span className="font-display text-2xl text-white">H-Plate</span>
            </Link>
            <p className="mt-4 text-[15px] leading-relaxed text-white/65 max-w-sm">
              The bus app for the Commonwealth of Dominica. Live buses, the
              official fare, and licensed H-bus drivers on routes across the
              island.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="H-Plate on Instagram"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/40 transition-colors"
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="H-Plate on Facebook"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/40 transition-colors"
              >
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className={headingClass}>The app</h4>
            <div className="flex flex-col gap-3">
              <a href="/#features" className={linkClass}>Features</a>
              <a href="/#routes" className={linkClass}>Routes</a>
              <a href="/#drivers" className={linkClass}>For drivers</a>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>App Store</a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>Google Play</a>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className={headingClass}>Company</h4>
            <div className="flex flex-col gap-3">
              <Link href="/advertisers" className={linkClass}>Advertise</Link>
              <Link href="/support" className={linkClass}>Support</Link>
              <a href="mailto:support@h-plate.com" className={linkClass}>support@h-plate.com</a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className={headingClass}>Legal</h4>
            <div className="flex flex-col gap-3">
              <Link href="/privacy" className={linkClass}>Privacy Policy</Link>
              <Link href="/terms" className={linkClass}>Terms of Service</Link>
              <Link href="/delete-account" className={linkClass}>Delete your account</Link>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <p className="text-[13px] text-white/50">
            &copy; {year} Nex Paradise Ltd. H-Plate is operated by Nex Paradise Ltd, Commonwealth of Dominica.
          </p>
          {/* Plain <a>, not <Link>: a full page load lets Cloudflare Access gate /admin before the app renders. */}
          <a href="/admin" className="text-[13px] text-white/30 hover:text-white/60 transition-colors no-underline">
            Operator sign-in
          </a>
        </div>
        <p className="mt-4 text-[12px] leading-relaxed text-white/50 max-w-4xl">
          H-Plate is a technology platform that connects passengers with independent, licensed H-bus drivers.
          It does not employ drivers, operate buses or provide transport.
        </p>
        <p className="mt-4 text-[12px] leading-relaxed text-white/35 max-w-4xl">
          Photos: Scotts Head and Soufrière Bay by Postdlf (<a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/60">CC BY-SA 3.0</a>);
          {" "}Bay Street, Portsmouth by Tournasol7 (<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/60">CC BY 4.0</a>);
          {" "}Trafalgar Falls by Nelro (public domain). Via Wikimedia Commons, cropped.
        </p>
      </div>
    </footer>
  );
}
